/*********************
 * Pass base class *
 *
 * This class is extended and instantiated by the passes.
 * Passes each model a decision process.
 * Runs represent one walk-through of that decision process, based on the state passed in to the run.
 *********************/

// This config determines whether we should keep track of why the pass kicks out or chooses an intervention
import { LOG_DECISION_CONTEXT } from '../../config';
import { disabledInterventionTypes } from '../../constants';
import shuffle from '../utils/shuffle';
import { getWordMetadata } from '../../utils/metadataUtils';

export default class Pass {
  constructor() {
    this.log = {
      selected: null,
      rationale: null,
      qualified: {},
      disqualified: {},
    };

    // vars to use across multiple runs
    this.interventions = []; // an array to keep track of Interventions which contain Rules (note: these are objects, distinct from interventionType enums)
    this.label = 'GENERIC_PASS'; // Pass label is used for logging, and overridden by passes that extend the base class

    // run specific vars (these will be reset for each run)
    this.rationaleLog = []; // for each run through a pass, use an class-scoped array to track rationale for decisions
    this.runStillValid = true; // for each run, keep track of whether the run is still valid (whether we should return an intervention)
    this.selectedInterventionType = null; // for each run, keep track of selected intervention
    this.validInterventionTypes = []; // for each run, keeps track of all valid interventions
  }

  // Add the intervention if it hasn't been disabled
  addIntervention(intervention) {
    if (intervention && !disabledInterventionTypes[intervention.type]) {
      this.interventions.push(intervention);
    }
  }

  // resets class vars for another next run
  resetForNextRun() {
    this.runStillValid = true;
    this.selectedInterventionType = null;
    this.rationaleLog = [];
    this.validInterventionTypes = [];
    this.log = {
      selected: null,
      rationale: null,
      qualified: {},
      disqualified: {},
    };
  }

  // Marks a run-through of a pass as invalid
  // Logs corresponding message if logging is enabled
  markRunInvalid(rationale) {
    LOG_DECISION_CONTEXT &&
      this.rationaleLog.push(this.label + '_' + rationale);
    this.runStillValid = false;
  }

  markInterventionAsApplicable(interventionType, rationale) {
    LOG_DECISION_CONTEXT &&
      this.rationaleLog.push(this.label + '_' + rationale);
    this.validInterventionTypes.push(interventionType);
  }

  getValidInterventions() {
    return this.validInterventionTypes;
  }

  // Stores selected intervention in a class variable.
  // Logs corresponding message if logging is enabled
  selectIntervention(interventionType, rationale) {
    LOG_DECISION_CONTEXT &&
      this.rationaleLog.push(this.label + '_' + rationale);
    this.selectedInterventionType = interventionType;

    this.log.selected = interventionType;
    this.log.rationale = rationale;
  }

  // Stores fallback intervention for client in a class variable.
  setFallbackIntervention(interventionType) {
    this.fallbackInterventionType = interventionType;
  }

  ////// Run Qualification Rules
  // This method is used by passes which evaluate Interventions with Rules
  // It determines which Interventions describe a valid interventionType,
  // for the given run through this pass.
  qualifyInterventions(state) {
    // add disabled interventions passed through in the interactions object to the invalid interventions so that we don't select them
    let invalidInterventions = state.invalidInterventions || [];
    let disabledInterventionTypes = state.disabledInterventionTypes || [];

    let decisionContext = []; // LOGGING VAR - builds up decision context for this run through interventions/rules
    let validTypeByInterventionRules = this.interventions.reduce(
      ((validTypes, intervention) => {
        const interventionType = intervention.getType();

        const interventionDisabled =
          disabledInterventionTypes.includes(interventionType);
        const interventionHasOcurredOnWord = invalidInterventions.some(
          (type) => {
            // Count any intervention within a group having occurred on this word as invalid, i.e. elkoninBox2 should be invalid if elkoninBox1 has been given on this word
            // To accomplish, strip numbers from the types we want to compare
            return (
              type.replace(/[0-9]/g, '') ===
              interventionType.replace(/[0-9]/g, '')
            );
          }
        );

        if (interventionDisabled) {
          LOG_DECISION_CONTEXT &&
            decisionContext.push(
              'DISQUALIFICATION RULE - INTERVENTION was disabled by client or configs'
            );
          this.log.disqualified[
            interventionType
          ] = `DISABLED_INTERVENTION - intervention was disabled by client or configs`;
        } else if (interventionHasOcurredOnWord) {
          LOG_DECISION_CONTEXT &&
            decisionContext.push(
              'DISQUALIFICATION RULE - INTERVENTION/WORD pair has already occurred during this activity'
            );
          this.log.disqualified[
            interventionType
          ] = `REPEAT_INTERVENTION_WORD_PAIR - intervention/word pair already occurred in activity`;
        } else {
          // If intervention type hasn't been disqualified
          const [isQualified, interventionDecisionContext, log] =
            intervention.runRules(state);
          if (isQualified) {
            validTypes.push(interventionType);
            this.log.qualified[interventionType] = log.rules;
          } else {
            this.log.disqualified[interventionType] = log.rules;
          }

          LOG_DECISION_CONTEXT &&
            decisionContext.push(interventionDecisionContext);
        }

        return validTypes;
      }).bind(this),
      []
    );

    LOG_DECISION_CONTEXT &&
      this.rationaleLog.push({
        [this.label + '_QUALIFICATION_RULES']: decisionContext,
      });
    this.validInterventionTypes.push(...validTypeByInterventionRules);
  }

  ////// Select Best Valid Intervention By Ready Score
  // This method is used by passes which validate Interventions with Rules.
  // It picks the valid interventionType with the highest READY score.
  selectBestValidInterventionByReadyScore(state) {
    // TODO identify best based on sort by ready score
    let selectedIntervention = null;
    let highestReadyScore = 0;
    let multipleSelections = false;
    let shuffledValidInterventions = shuffle(
      this.validInterventionTypes.slice()
    );
    shuffledValidInterventions.forEach((interventionType) => {
      if (!selectedIntervention) {
        selectedIntervention = interventionType;
        highestReadyScore = state.scores.ready[interventionType];
      } else {
        multipleSelections = true;
        if (state.scores.ready[interventionType] > highestReadyScore) {
          selectedIntervention = interventionType;
          highestReadyScore = state.scores.ready[interventionType];
        }
      }
    });
    let logMessage = multipleSelections
      ? 'selected according to highest READY score: ' + highestReadyScore
      : 'selected the only qualifying intervention in pass. readyScore: ' +
        highestReadyScore;
    if (selectedIntervention) {
      this.selectIntervention(
        selectedIntervention,
        'SELECTION_RULE - ' + logMessage
      );
    }
  }

  selectBestValidInterventionByReadyScoreDiscountingSpellout(state) {
    // TODO identify best based on sort by ready score
    let selectedIntervention = null;
    let highestReadyScore = 0;
    let multipleSelections = false;
    let shuffledValidInterventions = shuffle(
      this.validInterventionTypes.slice()
    );
    if (shuffledValidInterventions.length > 1) {
      // if spelloutBefore exists
      if (shuffledValidInterventions.includes('spelloutBefore')) {
        // remove spelloutBefore
        let index = shuffledValidInterventions.indexOf('spelloutBefore');
        shuffledValidInterventions.splice(index, 1);
      }
    }
    if (shuffledValidInterventions.length > 1) {
      // if spellout exists
      if (shuffledValidInterventions.includes('spellout')) {
        // remove spellout
        let index = shuffledValidInterventions.indexOf('spellout');
        shuffledValidInterventions.splice(index, 1);
      }
    }

    shuffledValidInterventions.forEach((interventionType) => {
      if (!selectedIntervention) {
        selectedIntervention = interventionType;
        highestReadyScore = state.scores.ready[interventionType];
      } else {
        multipleSelections = true;
        if (state.scores.ready[interventionType] > highestReadyScore) {
          selectedIntervention = interventionType;
          highestReadyScore = state.scores.ready[interventionType];
        }
      }
    });
    let logMessage = multipleSelections
      ? 'selected according to highest READY score: ' + highestReadyScore
      : 'selected the only qualifying intervention in pass. readyScore: ' +
        highestReadyScore;
    if (selectedIntervention) {
      this.selectIntervention(
        selectedIntervention,
        'SELECTION_RULE - ' + logMessage
      );
    }
  }

  // Use the RankedInterventionSelector instance on the state object to draw an intervention
  selectValidInterventionByRankedSelector(state) {
    const {
      wcpm,
      isEarlyReader: isEnglishEarlyReader,
      isSpanishEarlyReader,
      locale,
      wordIndex,
      endOfPhrase,
      phraseInterventionWeights,
      isValidRunForV2,
      interventionBias,
    } = state;

    const isEarlyReader =
      (locale.toLowerCase() === 'en-us' && isEnglishEarlyReader) ||
      (locale.toLowerCase() === 'es-mx' && isSpanishEarlyReader);

    const wordMetadata = getWordMetadata(state.word, state);
    if (!wordMetadata) {
      this.log.noMetadata = true;
      LOG_DECISION_CONTEXT &&
        this.rationaleLog.push(
          this.label +
            ' - ' +
            `No word metadata. WCPM: ${wcpm}. isEarlyReader: ${isEarlyReader}`
        );
      return;
    }

    const isInterventionSupported = (interventionType) => {
      return this.getValidInterventions().includes(interventionType);
    };

    state.rankedInterventionSelector.setIsInterventionSupported(
      isInterventionSupported
    );

    if (isValidRunForV2) {
      // V2 intervention selection
      const v2InterventionWeights = phraseInterventionWeights[wordIndex];

      let intervention = state.rankedInterventionSelector.drawInterventionV2(
        v2InterventionWeights
      );

      // interventionBias is an interventionType string that is passed in from the client to bias the intervention selection.
      // It is used for development testing purposes only
      if (
        interventionBias &&
        Object.keys(v2InterventionWeights).includes(interventionBias) &&
        isInterventionSupported(interventionBias)
      ) {
        intervention = interventionBias;
      }

      if (intervention) {
        const logMessage = `${intervention}. V2: true. WCPM: ${wcpm}. isEarlyReader: ${isEarlyReader}. wordIndex: ${wordIndex}. locale: ${locale}. endOfPhrase: ${endOfPhrase}`;

        this.selectIntervention(intervention, 'SELECTION_RULE - ' + logMessage);
      } else {
        this.log.noValidV2Draw = true;
        LOG_DECISION_CONTEXT &&
          this.rationaleLog.push(
            this.label +
              ' - ' +
              `No valid intervention from V2 draw. WCPM: ${wcpm}. isEarlyReader: ${isEarlyReader}. wordIndex: ${wordIndex}. locale: ${locale}. endOfPhrase: ${endOfPhrase}`
          );
      }
      return;
    } else {
      // V1 intervention selection
      if (!wordMetadata.InterventionSelectionMetadata) {
        this.log.noClusterMetadata = true;
        LOG_DECISION_CONTEXT &&
          this.rationaleLog.push(
            this.label +
              ' - ' +
              `No intervention selection metadata. WCPM: ${wcpm}. isEarlyReader: ${isEarlyReader}`
          );
        return;
      }

      const wordInterventionMetadata =
        wordMetadata.InterventionSelectionMetadata;

      const interventionGroup =
        state.rankedInterventionSelector.selectInterventionGroup(
          wordInterventionMetadata,
          wcpm,
          isEarlyReader
        );

      if (!interventionGroup) {
        this.log.noClusterAssigned = true;
        this.rationaleLog.push(
          this.label +
            ' - ' +
            `No intervention selection metadata intervention group. WCPM: ${wcpm}. isEarlyReader: ${isEarlyReader}`
        );
        return;
      }

      const interventionItem =
        state.rankedInterventionSelector.drawIntervention(interventionGroup);

      if (interventionItem) {
        const logMessage = `${interventionItem.codename}. V2: false. Group: ${interventionGroup}. Rank: ${interventionItem.rank}. WER: ${wordInterventionMetadata.wer}. WCPM: ${wcpm}. isEarlyReader: ${isEarlyReader}`;

        this.selectIntervention(
          interventionItem.codename,
          'SELECTION_RULE - ' + logMessage
        );
      } else {
        this.log.noValidClusterDraw = true;
        LOG_DECISION_CONTEXT &&
          this.rationaleLog.push(
            this.label +
              ' - ' +
              `No valid intervention from draw. Group: ${interventionGroup}. WCPM: ${wcpm}. isEarlyReader: ${isEarlyReader}`
          );
      }
    }
  }

  // Returns the selected intervention and logging
  getRunResults() {
    if (!this.selectedInterventionType) {
      this.runStillValid = false;
    }

    this.log.selected = this.selectedInterventionType;

    return {
      selectedInterventionType: this.selectedInterventionType,
      fallbackInterventionType: this.fallbackInterventionType,
      log: this.log,
      decisionContext: {
        interventionSelected: this.runStillValid, // bool, whether the pass was "valid", e.g. resulted in an intervention selection
        rationales: this.rationaleLog, // rationale for decision(s) made during this pass
      },
    };
  }
}

/******
* Footnote: Any concrete instantiation of the "pass" class needs to implement a "run" method.
* "run" methods should:
* 1. consume State as a prop
* 2. call resetForNextRun() before processing each run
* 3. determine whether to mark the "run" invalid based on State
* 4. If valid, select an intervention
* 5. return the run results
* In code,
export default class AnActualPass extends Pass {
  run(state) {
    super.resetForNextRun();

    // logic for deciding whether to mark the "run" invalid, given the state, goes here
    // Then, logic for selecting an intervention goes here

    return super.getRunResults();
  }
}
******/
